/**
 * Activity items the person dismissed: event id → expiry, kept two hours in
 * browser storage (a per-viewer convenience), shared by the Workspace page
 * and the Chats side column's dock.
 */
import { useCallback, useState } from 'react';

const DISMISS_KEY = 'om_ws_dismissed';
const DISMISS_MS = 2 * 60 * 60 * 1000;

function readDismissed(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}') as Record<string, number>;
    const now = Date.now();
    return Object.fromEntries(Object.entries(raw).filter(([, exp]) => typeof exp === 'number' && exp > now));
  } catch {
    return {};
  }
}

function writeDismissed(map: Record<string, number>) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable */
  }
}

export function useDismissed() {
  const [dismissed, setDismissed] = useState<Record<string, number>>(readDismissed);
  const dismiss = useCallback((ids: string[]) => {
    setDismissed((prev) => {
      const exp = Date.now() + DISMISS_MS;
      const next = { ...prev };
      for (const id of ids) next[id] = exp;
      writeDismissed(next);
      return next;
    });
  }, []);
  return { dismissed, dismiss };
}
